import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button, HeadingText } from "../../../shared/ui";
import { KybRequirement } from "../types";
import { isImageDocument } from "./kybShowReviewShared";

type KybComplianceDocumentPreviewModalProps = {
  open: boolean;
  document: KybRequirement | null;
  documentUrl?: string;
  canGoToPrevious?: boolean;
  canGoToNext?: boolean;
  onClose: () => void;
  onGoToPrevious?: () => void;
  onGoToNext?: () => void;
};

const KybComplianceDocumentPreviewModal = ({
  open,
  document,
  documentUrl,
  canGoToPrevious = false,
  canGoToNext = false,
  onClose,
  onGoToPrevious,
  onGoToNext,
}: KybComplianceDocumentPreviewModalProps) => {
  if (!open || !documentUrl) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-[rgba(13,16,29,0.82)] p-4">
      <div className="relative flex h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-[20px] bg-white shadow-[0_24px_80px_rgba(13,16,29,0.32)]">
        <div className="flex items-center justify-between border-b border-[#E4E7F4] px-5 py-4">
          <div className="flex min-w-0 items-center gap-4">
            <HeadingText className="truncate aku-heading-h5-medium text-[#161827]">
              {document?.key || "Document preview"}
            </HeadingText>
            <div className="flex shrink-0 items-center gap-0">
              <button
                type="button"
                disabled={!canGoToPrevious}
                onClick={onGoToPrevious}
                className="grid h-9 w-9 place-items-center rounded-l-[7.228px] rounded-r-none border-l-[0.941px] border-r border-t-[0.941px] border-b-[0.941px] border-[#E4E7F4] bg-white text-[#6F739B] hover:bg-[#FDFDFF] disabled:cursor-not-allowed disabled:text-[#C0C5E3] disabled:hover:bg-white"
                aria-label="Previous document"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                type="button"
                disabled={!canGoToNext}
                onClick={onGoToNext}
                className="grid h-9 w-9 place-items-center rounded-r-[7.228px] rounded-l-none border-r-[0.941px] border-l-0 border-t-[0.941px] border-b-[0.941px] border-[#E4E7F4] bg-white text-[#6F739B] hover:bg-[#FDFDFF] disabled:cursor-not-allowed disabled:text-[#C0C5E3] disabled:hover:bg-white"
                aria-label="Next document"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              type="button"
              variant="outline"
              className="h-10 rounded-[8px] border-[#E4E7F4] bg-white px-4 aku-body-small-medium text-[#161827] hover:bg-[#F7F8FD]"
              onClick={() =>
                window.open(documentUrl, "_blank", "noopener,noreferrer")
              }
            >
              Open in new tab
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="h-10 rounded-[8px] border-[#E4E7F4] bg-white px-4 aku-body-small-medium text-[#6F739B] hover:bg-[#F7F8FD]"
            >
              Close
            </Button>
          </div>
        </div>

        <div className="flex-1 overflow-auto bg-[#F7F8FD] p-4">
          {isImageDocument(documentUrl) ? (
            <div className="flex min-h-full items-center justify-center">
              <img
                src={documentUrl}
                alt={document?.key || "KYB document"}
                className="h-auto max-w-full object-contain"
              />
            </div>
          ) : (
            <iframe
              title={document?.key || "KYB document"}
              src={documentUrl}
              className="h-full min-h-[78vh] w-full rounded-[12px] bg-white"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default KybComplianceDocumentPreviewModal;
